import React, { FC, useMemo, useState, useCallback, useEffect } from 'react';
import { RouteComponentProps } from 'react-router';
import { Aggregation, AggregationContext } from '../Aggregation';
import Box from '@material-ui/core/Box';
import Header from '../components/Header';
import Divider from '@material-ui/core/Divider';
import TabNavigation from '../../sensor/TabNavigation';
import { Route, Switch, Redirect } from 'react-router-dom';
import ORUtilizationAnalytics from '../ORUtilizationAnalytics';
import PhysicianUtilizationAnalytics from '../PhysicianUtilizationAnalytics';
import AnesthesiologistUtilizationAnalytics from '../AnesthesiologistUtilizationAnalytics';
import PacuPhysiciainUtilizationAnalytics from '../PacuPhysiciainUtilizationAnalytics';
import ORScheduleAnalytics from '../ORScheduleAnalytics';
import GeneralAnalytics from '../GeneralAnalytics';
import ExecutiveAnalytics, {
  TimePeriodPicker,
  PeriodParams,
  TimePeriod,
  currentPeriodParams,
  parsePeriodParams,
  periodParamsToSearch,
  periodParamsToStartDate,
} from '../ExecutiveAnalytics';
import { makeStyles } from '@material-ui/core/styles';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import ToggleButton from '@material-ui/lab/ToggleButton';
import Filters from '../../analytics/Filters';
import { analyticsConfigQuery } from '@/graph/dashboard';
import { useQuery } from '@apollo/client';
import { EXECUTIVE_KPI_EARLIEST_DATE_QUERY, ExecutiveKpiEarliestDateData } from '@/graph/group-performance';
import { LocalDate, Period } from '@js-joda/core';
import { Range } from '../../../core/DatePicker';

interface AggregationPickerProps {
  value: Aggregation;
  onChange: (value: Aggregation) => void;
}

const AggregationPicker = ({ value, onChange }: AggregationPickerProps) => {
  const handleAggregationChange = (_: React.MouseEvent<HTMLElement>, value: any) => {
    const parsed = Aggregation.safeParse(value);
    onChange(parsed.success ? parsed.data : 'average');
  };

  return (
    <Box display="flex" alignItems="center" mr={2}>
      <ToggleButtonGroup size="small" value={value} exclusive onChange={handleAggregationChange}>
        <ToggleButton value="average">Average</ToggleButton>
        <ToggleButton value="median">Median</ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
};

const tabs = (match, pacuAnalytics, anesthesiologistUtilizationAnalytics) => {
  const baseTabs = [
    {
      id: 'executive',
      name: 'Executive',
      pathname: `${match.url}/executive`,
    },
    {
      id: 'general',
      name: 'General',
      pathname: `${match.url}/general`,
    },
    {
      id: 'operating-room',
      name: 'Analysis by OR',
      pathname: `${match.url}/operating-room`,
    },
    {
      id: 'physician',
      name: 'Analysis by Physician',
      pathname: `${match.url}/physician`,
    },
  ];

  const conditionalTabs: Array<{ id: string; name: string; pathname: string }> = [];

  if (pacuAnalytics) {
    conditionalTabs.push({
      id: 'pacu',
      name: 'PACU',
      pathname: `${match.url}/pacu`,
    });
  }

  if (anesthesiologistUtilizationAnalytics) {
    conditionalTabs.push({
      id: 'anesthesiologist',
      name: 'Analysis by Anesthesiologist',
      pathname: `${match.url}/anesthesiologist`,
    });
  }

  conditionalTabs.push({
    id: 'or-schedule',
    name: 'OR Case Flow',
    pathname: `${match.url}/or-schedule`,
  });

  return [...baseTabs, ...conditionalTabs];
};

const dateToLocalDate = (date: Date) => LocalDate.of(date.getFullYear(), date.getMonth() + 1, date.getDate());

const periodParamsToDateRange = (params: PeriodParams, today: LocalDate) => {
  const start = LocalDate.parse(periodParamsToStartDate(params));

  switch (params.t) {
    case 'd':
      return Range.of(start, Period.ofDays(1), today);
    case 'w':
      return Range.of(start, Period.ofWeeks(1), today);
    case 'm':
      return Range.of(start, Period.ofMonths(1), today);
    case 'q':
      return Range.of(start, Period.ofMonths(3), today);
    case 'y':
      return Range.of(start, Period.ofYears(1), today);
    case 'c': {
      // Inclusive custom range: [from, to]. Range periods are exclusive of the end,
      // so add a day to include the final selected date.
      const end = params.to ? LocalDate.parse(params.to).plusDays(1) : start.plusDays(1);
      return Range.between(start, end, today);
    }
  }
};

const periodParamsToFilterDates = (params: PeriodParams, today: LocalDate) => ({
  dateRange: periodParamsToDateRange(params, today),
  selectedDate: LocalDate.parse(periodParamsToStartDate(params)),
});

const filterHasPeriodDates = (filter: any, dates: { dateRange: any; selectedDate: LocalDate }) =>
  Boolean(filter?.dateRange?.equals?.(dates.dateRange)) && Boolean(filter?.selectedDate?.equals?.(dates.selectedDate));

const OR_SCHEDULE_DISABLED_PERIODS: TimePeriod[] = ['w', 'm', 'q', 'y', 'c'];
const OR_SCHEDULE_DISABLED_PERIOD_TOOLTIP =
  'Week, month, quarter, year, and custom ranges are not available for OR Schedule.';
const STANDARD_ANALYTICS_EARLIEST_DATE = '2017-01-01';

const AnalyticsV2: FC<
  {
    filter: any;
    setFilter: any;
    isBusinessManager: boolean;
    physicianIdsFromMetadata: number[];
    pacuAnalytics: boolean;
    anesthesiologistUtilizationAnalytics: boolean;
  } & RouteComponentProps
> = ({
  filter,
  setFilter,
  isBusinessManager,
  history,
  location,
  match,
  physicianIdsFromMetadata,
  pacuAnalytics,
  anesthesiologistUtilizationAnalytics,
}) => {
  const classes = useStyles();

  const now = useMemo(() => new Date(), []);
  const today = useMemo(() => dateToLocalDate(now), [now]);
  const isOrScheduleTab = location.pathname === `${match.url}/or-schedule`;
  const analyticsPathname = location.pathname === match.url ? `${match.url}/executive` : location.pathname;
  const executivePathname = `${match.url}/executive`;
  const isExecutiveTab =
    analyticsPathname === executivePathname || analyticsPathname.startsWith(`${executivePathname}/`);
  const periodParams = useMemo(() => parsePeriodParams(location.search, now), [location.search, now]);
  const periodFilterDates = useMemo(() => periodParamsToFilterDates(periodParams, today), [periodParams, today]);
  const syncedFilter = useMemo(
    () => (filterHasPeriodDates(filter, periodFilterDates) ? filter : { ...filter, ...periodFilterDates }),
    [filter, periodFilterDates]
  );

  const { data: earliestDateData, loading: earliestDateLoading } = useQuery<ExecutiveKpiEarliestDateData>(
    EXECUTIVE_KPI_EARLIEST_DATE_QUERY
  );
  const earliestDate = earliestDateData?.executiveKpiEarliestDate ?? undefined;
  const periodPickerEarliestDate =
    isExecutiveTab || isOrScheduleTab ? earliestDate : STANDARD_ANALYTICS_EARLIEST_DATE;

  const handleFilterChange = useCallback(
    (nextFilter: any, nextPeriodParams: PeriodParams = periodParams) => {
      setFilter(nextFilter);
      const currentSearch = window.location.search || location.search;
      const newSearch = periodParamsToSearch(nextPeriodParams, currentSearch);
      history.replace(`${analyticsPathname}?${newSearch}`);
    },
    [analyticsPathname, history, location.search, periodParams, setFilter]
  );

  const handlePeriodChange = useCallback(
    (params: PeriodParams) => {
      handleFilterChange({ ...syncedFilter, ...periodParamsToFilterDates(params, today) }, params);
    },
    [handleFilterChange, syncedFilter, today]
  );

  useEffect(() => {
    if (isOrScheduleTab && periodParams.t !== 'd') return;
    if (!filterHasPeriodDates(filter, periodFilterDates)) {
      handleFilterChange(syncedFilter, periodParams);
    }
  }, [filter, handleFilterChange, isOrScheduleTab, periodFilterDates, periodParams, syncedFilter]);

  useEffect(() => {
    if (!isOrScheduleTab || periodParams.t === 'd') return;
    handlePeriodChange(currentPeriodParams('d', now));
  }, [handlePeriodChange, isOrScheduleTab, now, periodParams.t]);

  useEffect(() => {
    if (!isExecutiveTab) return;
    if (earliestDateLoading || earliestDate || periodParams.t === 'd') return;
    handlePeriodChange(currentPeriodParams('d', now));
  }, [earliestDate, earliestDateLoading, handlePeriodChange, isExecutiveTab, now, periodParams.t]);

  useEffect(() => {
    if (!isExecutiveTab || earliestDateLoading || !earliestDate) return;
    const selectedPeriodEnd = periodFilterDates.dateRange.lastIncluded().toString();
    if (selectedPeriodEnd < earliestDate) {
      handlePeriodChange(currentPeriodParams(periodParams.t, now));
    }
  }, [
    earliestDate,
    earliestDateLoading,
    handlePeriodChange,
    isExecutiveTab,
    now,
    periodFilterDates,
    periodParams.t,
  ]);

  const initialAggregation = useMemo(() => {
    const aggregation = localStorage.getItem('analytics-aggregation');
    const parsed = Aggregation.safeParse(aggregation);
    return parsed.success ? parsed.data : 'average';
  }, []);

  const [aggregation, setAggregation] = useState<Aggregation>(initialAggregation);

  const handleAggregationChange = (value: Aggregation) => {
    setAggregation(value);
    localStorage.setItem('analytics-aggregation', value);
  };
  const hasPhysicianIds = physicianIdsFromMetadata?.length > 0;

  const analyticsConfig = useQuery(analyticsConfigQuery);
  const totalPatientsThreshold = analyticsConfig?.data?.analyticsConfiguration?.totalPatientsThreshold;
  const waitingRoomTimeThreshold = analyticsConfig?.data?.analyticsConfiguration?.waitingRoomTimeThreshold;
  const preopTimeThreshold = analyticsConfig?.data?.analyticsConfiguration?.preopTimeThreshold;
  const readyForAnesthesiaThreshold = analyticsConfig?.data?.analyticsConfiguration?.readyForAnesthesiaThreshold;
  const readyForORThreshold = analyticsConfig?.data?.analyticsConfiguration?.readyForORThreshold;
  // analyticsConfig?.data?.analyticsConfiguration?.orUtilThreshold,
  const orTimeThreshold = analyticsConfig?.data?.analyticsConfiguration?.orTimeThreshold;
  const cleaningTimeThreshold = analyticsConfig?.data?.analyticsConfiguration?.cleaningTimeThreshold;
  const closingTimeThreshold = analyticsConfig?.data?.analyticsConfiguration?.closingTimeThreshold;
  const popiTimeThreshold = analyticsConfig?.data?.analyticsConfiguration?.popiTimeThreshold;
  const pacuTimeThreshold = analyticsConfig?.data?.analyticsConfiguration?.pacuTimeThreshold;
  const postOpTimeThreshold = analyticsConfig?.data?.analyticsConfiguration?.postOpTimeThreshold;
  const popiMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.popiMinThreshold;
  const popiMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.popiMaxThreshold;
  const wrMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.wrMinThreshold;
  const wrMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.wrMaxThreshold;
  const preOpMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.preOpMinThreshold;
  const preOpMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.preOpMaxThreshold;
  const cleaningMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.cleaningMinThreshold;
  const cleaningMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.cleaningMaxThreshold;
  const pacuMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.pacuMinThreshold;
  const pacuMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.pacuMaxThreshold;
  const postOpMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.postOpMinThreshold;
  const postOpMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.postOpMaxThreshold;
  const inOrMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.inOrMinThreshold;
  const inOrMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.inOrMaxThreshold;
  const pipoMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.pipoMinThreshold;
  const pipoMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.pipoMaxThreshold;
  const preOpToDischargedMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.preOpToDischargedMinThreshold;
  const preOpToDischargedMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.preOpToDischargedMaxThreshold;
  const inSurgeryMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.inSurgeryMinThreshold;
  const inSurgeryMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.inSurgeryMaxThreshold;
  const closingMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.closingMinThreshold;
  const closingMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.closingMaxThreshold;
  const procedureCompleteMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.procedureCompleteMinThreshold;
  const procedureCompleteMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.procedureCompleteMaxThreshold;
  const orReadyMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.orReadyMinThreshold;
  const orReadyMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.orReadyMaxThreshold;
  const samePhysicianORTurnover = analyticsConfig?.data?.analyticsConfiguration?.samePhysicianORTurnover;
  const piToStartMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.piToStartMinThreshold;
  const piToStartMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.piToStartMaxThreshold;
  const poToCutMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.poToCutMinThreshold;
  const poToCutMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.poToCutMaxThreshold;
  const startToCloseMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.startToCloseMinThreshold;
  const startToCloseMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.startToCloseMaxThreshold;
  const physicianPopiMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.physicianPopiMinThreshold;
  const physicianPopiMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.physicianPopiMaxThreshold;
  const fcotsGracePeriod = analyticsConfig?.data?.analyticsConfiguration?.fcotsGracePeriod;
  const piToPhyiMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.piToPhyiMinThreshold;
  const piToPhyiMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.piToPhyiMaxThreshold;
  const closingCompleteToPatientOutMinThreshold =
    analyticsConfig?.data?.analyticsConfiguration?.closingCompleteToPatientOutMinThreshold;
  const closingCompleteToPatientOutMaxThreshold =
    analyticsConfig?.data?.analyticsConfiguration?.closingCompleteToPatientOutMaxThreshold;
  const wrInToDischargeMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.wrInToDischargeMinThreshold;
  const wrInToDischargeMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.wrInToDischargeMaxThreshold;

  const thresholds = {
    totalPatientsThreshold: totalPatientsThreshold,
    waitingRoomTimeThreshold: waitingRoomTimeThreshold,
    preopTimeThreshold: preopTimeThreshold,
    readyForAnesthesiaThreshold: readyForAnesthesiaThreshold,
    readyForORThreshold: readyForORThreshold,
    orTimeThreshold: orTimeThreshold,
    cleaningTimeThreshold: cleaningTimeThreshold,
    closingTimeThreshold: closingTimeThreshold,
    popiTimeThreshold: popiTimeThreshold,
    pacuTimeThreshold: pacuTimeThreshold,
    postOpTimeThreshold: postOpTimeThreshold,
    pipoMinThreshold: pipoMinThreshold,
    pipoMaxThreshold: pipoMaxThreshold,
    popiMinThreshold: popiMinThreshold,
    popiMaxThreshold: popiMaxThreshold,
    wrMinThreshold: wrMinThreshold,
    wrMaxThreshold: wrMaxThreshold,
    preOpMinThreshold: preOpMinThreshold,
    preOpMaxThreshold: preOpMaxThreshold,
    cleaningMinThreshold: cleaningMinThreshold,
    cleaningMaxThreshold: cleaningMaxThreshold,
    pacuMinThreshold: pacuMinThreshold,
    pacuMaxThreshold: pacuMaxThreshold,
    postOpMinThreshold: postOpMinThreshold,
    postOpMaxThreshold: postOpMaxThreshold,
    preOpToDischargedMinThreshold: preOpToDischargedMinThreshold,
    preOpToDischargedMaxThreshold: preOpToDischargedMaxThreshold,
    inOrMinThreshold: inOrMinThreshold,
    inOrMaxThreshold: inOrMaxThreshold,
    inSurgeryMinThreshold: inSurgeryMinThreshold,
    inSurgeryMaxThreshold: inSurgeryMaxThreshold,
    closingMinThreshold: closingMinThreshold,
    closingMaxThreshold: closingMaxThreshold,
    procedureCompleteMinThreshold: procedureCompleteMinThreshold,
    procedureCompleteMaxThreshold: procedureCompleteMaxThreshold,
    orReadyMinThreshold: orReadyMinThreshold,
    orReadyMaxThreshold: orReadyMaxThreshold,
    samePhysicianORTurnover: samePhysicianORTurnover,
    piToStartMinThreshold: piToStartMinThreshold,
    piToStartMaxThreshold: piToStartMaxThreshold,
    poToCutMinThreshold: poToCutMinThreshold,
    poToCutMaxThreshold: poToCutMaxThreshold,
    startToCloseMinThreshold: startToCloseMinThreshold,
    startToCloseMaxThreshold: startToCloseMaxThreshold,
    physicianPopiMinThreshold: physicianPopiMinThreshold,
    physicianPopiMaxThreshold: physicianPopiMaxThreshold,
    fcotsGracePeriod: fcotsGracePeriod,
    piToPhyiMinThreshold: piToPhyiMinThreshold,
    piToPhyiMaxThreshold: piToPhyiMaxThreshold,
    closingCompleteToPatientOutMinThreshold: closingCompleteToPatientOutMinThreshold,
    closingCompleteToPatientOutMaxThreshold: closingCompleteToPatientOutMaxThreshold,
    wrInToDischargeMinThreshold: wrInToDischargeMinThreshold,
    wrInToDischargeMaxThreshold: wrInToDischargeMaxThreshold,
  };

  return (
    <Box className={classes.root}>
      <Header
        filter={syncedFilter}
        thresholds={thresholds}
        datePicker={
          <TimePeriodPicker
            periodParams={periodParams}
            earliestDate={periodPickerEarliestDate}
            hideRollupPeriodsWhenEmpty={isExecutiveTab && !earliestDateLoading}
            disabledPeriods={isOrScheduleTab ? OR_SCHEDULE_DISABLED_PERIODS : undefined}
            disabledPeriodTooltip={isOrScheduleTab ? OR_SCHEDULE_DISABLED_PERIOD_TOOLTIP : undefined}
            onChange={handlePeriodChange}
          />
        }
      />
      <Divider />
      <Switch>
        <Route
          path={`${match.path}/general`}
          render={() => (
            <Box className={classes.toolBar}>
              <Box className={classes.navAndAggregation}>
                <Box>
                  <TabNavigation
                    tabs={tabs(match, pacuAnalytics, anesthesiologistUtilizationAnalytics)}
                    location={location}
                  />
                </Box>
              </Box>
              <Box flex={1} alignSelf="stretch" className={classes.filters}>
                <Filters
                  onChange={handleFilterChange}
                  value={syncedFilter}
                  defaultValue={periodFilterDates}
                  reload={false}
                  dontReloadOn={['operationRooms']}
                  showOperationRoomsSelectInput
                  showPhysiciansSelectInput={!hasPhysicianIds}
                  hidePhysicianSelectInput
                  hideSpecialitySelectInput={hasPhysicianIds}
                  hideDateFilter
                />
              </Box>
            </Box>
          )}
        />
        <Route
          path={`${match.path}/operating-room`}
          render={() => (
            <Box className={classes.toolBar}>
              <Box className={classes.navAndAggregation}>
                <Box>
                  <TabNavigation
                    tabs={tabs(match, pacuAnalytics, anesthesiologistUtilizationAnalytics)}
                    location={location}
                  />
                </Box>
                <Box className={classes.buttonGroup}>
                  <AggregationPicker value={aggregation} onChange={handleAggregationChange} />
                </Box>
              </Box>
              <Box flex={1} alignSelf="stretch" className={classes.filters}>
                <Filters
                  onChange={handleFilterChange}
                  value={syncedFilter}
                  defaultValue={periodFilterDates}
                  reload={false}
                  dontReloadOn={['operationRooms']}
                  showOperationRoomsSelectInput
                  showPhysiciansSelectInput={!hasPhysicianIds}
                  hidePhysicianSelectInput
                  hideSpecialitySelectInput={hasPhysicianIds}
                  hideDateFilter
                />
              </Box>
            </Box>
          )}
        />
        <Route
          path={`${match.path}/physician`}
          render={() => (
            <Box className={classes.toolBar}>
              <Box className={classes.navAndAggregation}>
                <Box>
                  <TabNavigation
                    tabs={tabs(match, pacuAnalytics, anesthesiologistUtilizationAnalytics)}
                    location={location}
                  />
                </Box>
                <Box className={classes.buttonGroup}>
                  <AggregationPicker value={aggregation} onChange={handleAggregationChange} />
                </Box>
              </Box>
              <Box flex={1} alignSelf="stretch" className={classes.filters}>
                <Filters
                  onChange={handleFilterChange}
                  value={syncedFilter}
                  defaultValue={periodFilterDates}
                  reload={false}
                  dontReloadOn={['operationRooms']}
                  showOperationRoomsSelectInput
                  showPhysiciansSelectInput={!hasPhysicianIds}
                  hidePhysicianSelectInput
                  hideSpecialitySelectInput={hasPhysicianIds}
                  hideDateFilter
                />
              </Box>
            </Box>
          )}
        />
        {anesthesiologistUtilizationAnalytics && (
          <Route
            path={`${match.path}/anesthesiologist`}
            render={() => (
              <Box className={classes.toolBar}>
                <Box className={classes.navAndAggregation}>
                  <Box>
                    <TabNavigation
                      tabs={tabs(match, pacuAnalytics, anesthesiologistUtilizationAnalytics)}
                      location={location}
                    />
                  </Box>
                  <Box className={classes.buttonGroup}>
                    <AggregationPicker value={aggregation} onChange={handleAggregationChange} />
                  </Box>
                </Box>
                <Box flex={1} alignSelf="stretch" className={classes.filters}>
                  <Filters
                    onChange={handleFilterChange}
                    value={syncedFilter}
                    defaultValue={periodFilterDates}
                    reload={false}
                    dontReloadOn={['operationRooms']}
                    showOperationRoomsSelectInput
                    showPhysiciansSelectInput={!hasPhysicianIds}
                    hidePhysicianSelectInput
                    hideSpecialitySelectInput={hasPhysicianIds}
                    hideDateFilter
                  />
                </Box>
              </Box>
            )}
          />
        )}
        {pacuAnalytics && (
          <Route
            path={`${match.path}/pacu`}
            render={() => (
              <Box className={classes.toolBar}>
                <Box className={classes.navAndAggregation}>
                  <Box>
                    <TabNavigation
                      tabs={tabs(match, pacuAnalytics, anesthesiologistUtilizationAnalytics)}
                      location={location}
                    />
                  </Box>
                  <Box className={classes.buttonGroup}>
                    <AggregationPicker value={aggregation} onChange={handleAggregationChange} />
                  </Box>
                </Box>
                <Box flex={1} alignSelf="stretch" className={classes.filters}>
                  <Filters
                    onChange={handleFilterChange}
                    value={syncedFilter}
                    defaultValue={periodFilterDates}
                    reload={false}
                    dontReloadOn={['operationRooms']}
                    showOperationRoomsSelectInput
                    showPhysiciansSelectInput={!hasPhysicianIds}
                    hidePhysicianSelectInput
                    hideSpecialitySelectInput={hasPhysicianIds}
                    hideDateFilter
                  />
                </Box>
              </Box>
            )}
          />
        )}
        <Route
          path={`${match.path}/or-schedule`}
          render={() => (
            <Box className={classes.toolBar}>
              <Box className={classes.navAndAggregation}>
                <Box>
                  <TabNavigation
                    tabs={tabs(match, pacuAnalytics, anesthesiologistUtilizationAnalytics)}
                    location={location}
                  />
                </Box>
              </Box>
              <Box flex={1} alignSelf="stretch" className={classes.filters}>
                <Filters
                  onChange={handleFilterChange}
                  value={syncedFilter}
                  defaultValue={periodFilterDates}
                  reload={false}
                  dontReloadOn={['operationRooms', 'physicians', 'selectedDate']}
                  showOperationRoomsSelectInput
                  showPhysiciansSelectInput={!hasPhysicianIds}
                  hidePhysicianSelectInput
                  hideSpecialitySelectInput={hasPhysicianIds}
                  hideDateFilter
                  hideSingleDateFilter
                />
              </Box>
            </Box>
          )}
        />
        <Route
          path={`${match.path}/executive`}
          render={routeProps => (
            <Box className={classes.toolBar}>
              <Box className={classes.navAndAggregation}>
                <Box>
                  <TabNavigation
                    tabs={tabs(match, pacuAnalytics, anesthesiologistUtilizationAnalytics)}
                    location={routeProps.location}
                  />
                </Box>
              </Box>
            </Box>
          )}
        />
      </Switch>

      <AggregationContext.Provider value={aggregation}>
        <Switch>
          <Route
            path={`${match.path}/general`}
            render={() => (
              <GeneralAnalytics
                filter={syncedFilter}
                setFilter={handleFilterChange}
                isBusinessManager={isBusinessManager}
                thresholds={thresholds}
              />
            )}
          />
          <Route
            path={`${match.path}/operating-room`}
            render={() => <ORUtilizationAnalytics filter={syncedFilter} setFilter={handleFilterChange} />}
          />
          <Route
            path={`${match.path}/physician`}
            render={() => <PhysicianUtilizationAnalytics filter={syncedFilter} setFilter={handleFilterChange} />}
          />
          {anesthesiologistUtilizationAnalytics && (
            <Route
              path={`${match.path}/anesthesiologist`}
              render={() => (
                <AnesthesiologistUtilizationAnalytics filter={syncedFilter} setFilter={handleFilterChange} />
              )}
            />
          )}
          {pacuAnalytics && (
            <Route
              path={`${match.path}/pacu`}
              render={() => <PacuPhysiciainUtilizationAnalytics filter={syncedFilter} setFilter={handleFilterChange} />}
            />
          )}
          <Route
            path={`${match.path}/or-schedule`}
            render={() => <ORScheduleAnalytics filter={syncedFilter} setFilter={handleFilterChange} />}
          />
          <Route
            path={`${match.path}/executive`}
            render={routeProps => (
              <ExecutiveAnalytics
                filter={syncedFilter}
                setFilter={handleFilterChange}
                thresholds={thresholds}
                history={routeProps.history}
                location={routeProps.location}
              />
            )}
          />
          <Redirect
            from={match.path}
            exact
            to={{
              pathname: `${match.url}/executive`,
              search: location.search,
            }}
          />
        </Switch>
      </AggregationContext.Provider>
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  toolBar: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  navAndAggregation: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  loading: {
    position: 'absolute',
    margin: 'auto',
  },
  actions: {
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  tabs: {
    marginLeft: -theme.spacing(2),
    marginRight: -theme.spacing(2),
  },
  buttonGroup: {
    '&:empty': {
      display: 'none',
    },
    [theme.breakpoints.down('md')]: {
      order: 2,
    },
  },
  filters: {
    [theme.breakpoints.down('md')]: {
      order: 1,
    },
  },
}));

export default AnalyticsV2;
